const MAX_PROJECT_FAVORITES = 100;

export interface ProjectFavoritesFallback {
  favoriteProjectIds: string[];
  baseFavoriteProjectIds: string[] | null;
  updatedAtMs: number;
}

export function normalizeProjectFavoriteIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return Array.from(
    new Set(
      value.filter(
        (projectId): projectId is string =>
          typeof projectId === 'string' && projectId.trim().length > 0
      )
    )
  ).slice(0, MAX_PROJECT_FAVORITES);
}

export function orderProjectsByFavorites<T extends { id: string }>(
  projects: readonly T[],
  favoriteProjectIds: readonly string[]
): T[] {
  const favoriteProjectIdSet = new Set(favoriteProjectIds);
  const favoriteProjects: T[] = [];
  const otherProjects: T[] = [];

  for (const project of projects) {
    (favoriteProjectIdSet.has(project.id) ? favoriteProjects : otherProjects).push(project);
  }

  return [...favoriteProjects, ...otherProjects];
}

export function shouldRetryProjectFavoritesFallback(
  fallback: ProjectFavoritesFallback,
  profileFavoriteProjectIds: readonly string[],
  serverUpdatedAtMs: number,
  hasProfileFavorites: boolean
): boolean {
  if (favoriteProjectIdsMatch(fallback.favoriteProjectIds, profileFavoriteProjectIds)) {
    return false;
  }
  if (fallback.baseFavoriteProjectIds) {
    return favoriteProjectIdsMatch(fallback.baseFavoriteProjectIds, profileFavoriteProjectIds);
  }
  if (fallback.updatedAtMs > 0 && serverUpdatedAtMs > 0) {
    return fallback.updatedAtMs > serverUpdatedAtMs;
  }
  return !hasProfileFavorites;
}

export function rebaseProjectFavoritesFallback(
  fallback: ProjectFavoritesFallback,
  confirmedFavoriteProjectIds: readonly string[]
): ProjectFavoritesFallback {
  return {
    ...fallback,
    baseFavoriteProjectIds: normalizeProjectFavoriteIds(confirmedFavoriteProjectIds),
  };
}

function favoriteProjectIdsMatch(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}
